// Thin `useReducer` wrapper around lib/document-editor.ts's pure reducer —
// the only place this feature touches React state. Components dispatch
// through the bound callbacks below rather than importing the reducer
// directly, and read `exportRawText()` for the current document serialized
// back to X12 text (lib/serializer.ts), ready to hand to
// lib/file-io.ts's `downloadTextFile`.

import {useCallback, useMemo, useReducer} from 'react';
import {
  ediDocumentReducer,
  initialEdiDocumentState,
  type ElementPath,
} from '../lib/document-editor';
import {flattenEnvelope} from '../lib/envelope';
import {serialize} from '../lib/serializer';

export function useEdiDocument() {
  const [state, dispatch] = useReducer(
    ediDocumentReducer,
    initialEdiDocumentState
  );

  const loadRaw = useCallback(
    (text: string) => dispatch({type: 'LOAD_RAW', text}),
    []
  );

  const editElement = useCallback(
    (path: ElementPath, value: string) =>
      dispatch({type: 'EDIT_ELEMENT', path, value}),
    []
  );

  const selectTransaction = useCallback(
    (groupIndex: number, transactionIndex: number) =>
      dispatch({type: 'SELECT_TRANSACTION', groupIndex, transactionIndex}),
    []
  );

  const reset = useCallback(() => dispatch({type: 'RESET'}), []);

  /** Serializes the currently-loaded document back to raw X12 text, or
   * `undefined` when nothing is loaded — the caller (the page's Export
   * button) decides what "nothing to export" looks like. */
  const exportRawText = useCallback((): string | undefined => {
    if (state.status !== 'loaded' || !state.envelope || !state.delimiters)
      return undefined;
    return serialize(state.delimiters, flattenEnvelope(state.envelope));
  }, [state.status, state.envelope, state.delimiters]);

  return useMemo(
    () => ({
      state,
      loadRaw,
      editElement,
      selectTransaction,
      reset,
      exportRawText,
    }),
    [state, loadRaw, editElement, selectTransaction, reset, exportRawText]
  );
}
